// src/encounter/lib/patient-reported.ts
// The "Patient-reported" section of the patient summary: the pre-visit questionnaires the patient submitted (PHQ-9 first),
// read from QuestionBank `GET api/questionnaire-responses/by-patient/{patientId}?questionnaire={id}` (QuestionBank #183).
// Pure: no React, no fetch.
//
// RULINGS (orchestrator, 22 Oct 2026):
//   · the score shows AS STORED — no bands, no colours, no alerts;
//   · PHQ-9 item 9 (self-harm) shows its answer as stored, with no special handling (the safety route is Andrew's call);
//   · read-only: nothing here writes.
// The response shape is Workflows' PromQuestionnaireResponseBuilder: one item per question with ONE `valueCoding`, and the
// calculated total under the questionnaire's total item as `valueDecimal`.
import type { IpsEntry, IpsSection } from '@scripsteam/scrips-signal-ds';
import type { QRAnswerWire, QRItemWire, QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';

/** LOINC 44261-6, "PHQ-9 total score [Reported]": the PHQ-9 total item's linkId in the NLM LForms PHQ-9 (44249-1). */
export const PHQ9_TOTAL_LINK_ID = '/44261-6';

export const PATIENT_REPORTED_SECTION_ID = 'patient-reported';

/** `VITE_PREVISIT_QUESTIONNAIRE_IDS`: a comma list of QuestionBank questionnaire GUIDs. Blanks and duplicates dropped. */
export function parseQuestionnaireIds(raw: string | undefined | null): string[] {
  const ids = (raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return [...new Set(ids)];
}

export interface PatientReportedAnswer {
  linkId: string;
  question: string;
  answer: string;
}

export interface PatientReportedResponse {
  /** The answer set's head row id (unique per response). */
  id: string;
  /** "PHQ-9" when the response carries the PHQ-9 total item; else "Questionnaire". */
  title: string;
  /** The stored total, or null when the response stores none. */
  score: number | null;
  /** ISO date-time the patient submitted it, or null. */
  authored: string | null;
  answers: PatientReportedAnswer[];
}

function answerText(a: QRAnswerWire | undefined): string {
  if (!a) return '';
  if (a.valueCoding) return a.valueCoding.display ?? a.valueCoding.code ?? '';
  if (a.valueString != null) return a.valueString;
  if (a.valueInteger != null) return String(a.valueInteger);
  if (a.valueDecimal != null) return String(a.valueDecimal);
  if (a.valueBoolean != null) return a.valueBoolean ? 'Yes' : 'No';
  if (a.valueDateTime != null) return a.valueDateTime;
  return '';
}

function flatten(items: readonly QRItemWire[] | undefined): QRItemWire[] {
  return (items ?? []).flatMap((i) => [i, ...flatten(i.item)]);
}

/** One stored response as the card shows it. A response with no id is skipped (it cannot be told apart). */
export function toPatientReported(qr: QuestionnaireResponseResource): PatientReportedResponse | null {
  if (!qr.id) return null;
  const items = flatten(qr.item);
  const total = items.find((i) => i.linkId === PHQ9_TOTAL_LINK_ID);
  const stored = total?.answer?.[0];
  const score = stored?.valueDecimal ?? stored?.valueInteger ?? null;
  return {
    id: qr.id,
    title: total ? 'PHQ-9' : 'Questionnaire',
    score: typeof score === 'number' ? score : null,
    authored: qr.authored ?? null,
    answers: items
      .filter((i) => i.linkId !== PHQ9_TOTAL_LINK_ID && (i.answer?.length ?? 0) > 0)
      .map((i) => ({ linkId: i.linkId, question: i.text ?? i.linkId, answer: answerText(i.answer?.[0]) })),
  };
}

/**
 * True only for a set THIS patient entered: FHIR `source` = `Patient/{patientId}` (orchestrator, 22 Oct). A staff-entered
 * set on the same questionnaire has no such source and is never shown as patient-reported; a set with no source at all
 * (every set before the marker lands) is left out too, so the section stays empty rather than mislabel staff answers.
 */
export function isPatientEntered(qr: QuestionnaireResponseResource, patientId: string): boolean {
  const ref = qr.source?.reference?.trim();
  return !!ref && ref.toLowerCase() === `patient/${patientId}`.toLowerCase();
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Only a GUID can be a server-side filter: QuestionBank's `?questionnaire=` is GUID-typed, and it stores the questionnaire
 * id only when it parses as one. The vendored NLM PHQ-9 id `44249-1-x` (what the pre-visit Studio step sends, pm-react
 * #1324) is not, so it is matched on the response's own `questionnaire` reference instead.
 */
export function isGuid(id: string): boolean {
  return GUID.test(id);
}

/** The id a response answers: its `questionnaire` reference with any `Questionnaire/` prefix taken off. */
export function questionnaireIdOf(qr: QuestionnaireResponseResource): string | null {
  const ref = qr.questionnaire?.trim();
  if (!ref) return null;
  return ref.replace(/^Questionnaire\//i, '');
}

/**
 * Every response this patient entered for one of the configured questionnaires, de-duplicated by id, newest first (no
 * date sorts last). The questionnaire check also covers the unfiltered read a non-GUID id needs.
 */
export function toPatientReportedList(
  lists: readonly (readonly QuestionnaireResponseResource[])[],
  patientId: string,
  questionnaireIds: readonly string[],
): PatientReportedResponse[] {
  const wanted = new Set(questionnaireIds.map((id) => id.toLowerCase()));
  const byId = new Map<string, PatientReportedResponse>();
  for (const qr of lists.flat()) {
    if (!isPatientEntered(qr, patientId)) continue;
    if (!wanted.has(questionnaireIdOf(qr)?.toLowerCase() ?? '')) continue;
    const r = toPatientReported(qr);
    if (r && !byId.has(r.id)) byId.set(r.id, r);
  }
  const time = (r: PatientReportedResponse) => (r.authored ? Date.parse(r.authored) || 0 : 0);
  return [...byId.values()].sort((a, b) => time(b) - time(a));
}

/** "21 Oct 2026" in the practice's locale, or null for a missing or broken date. */
export function submittedOn(authored: string | null): string | null {
  if (!authored) return null;
  const d = new Date(authored);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "PHQ-9 · score 7", or "PHQ-9 · no score stored": the score exactly as stored, never banded. */
export function scoreLine(r: PatientReportedResponse): string {
  return r.score === null ? `${r.title} · no score stored` : `${r.title} · score ${r.score}`;
}

export const PATIENT_REPORTED_LOAD_FAILED = 'The patient’s questionnaires could not be loaded. Reopen the visit to try again.';

/**
 * The section the summary rail draws: one entry per response, each with "View answers". `null` = the read failed: one
 * line in words, never an empty list (an empty list would say the patient submitted nothing).
 */
export function patientReportedSection(
  responses: readonly PatientReportedResponse[] | null,
  onView: (id: string) => void,
): IpsSection {
  if (responses === null) {
    return {
      id: PATIENT_REPORTED_SECTION_ID,
      title: 'Patient-reported',
      accent: 'neutral',
      entries: [{ id: 'load-failed', primary: PATIENT_REPORTED_LOAD_FAILED }],
    };
  }
  const entries: IpsEntry[] = responses.map((r) => ({
    id: r.id,
    primary: scoreLine(r),
    secondary: submittedOn(r.authored) ? `Submitted ${submittedOn(r.authored)}` : undefined,
    actions: [{ label: 'View answers', onClick: () => onView(r.id) }],
  }));
  return { id: PATIENT_REPORTED_SECTION_ID, title: 'Patient-reported', accent: 'neutral', entries };
}

/**
 * The summary sections with the Patient-reported one placed after Active Problems (beside Flags, Allergies and Active
 * Problems), or last when there is no Active Problems section. An existing section with the same id is replaced.
 */
export function withPatientReportedSection(sections: readonly IpsSection[], section: IpsSection): IpsSection[] {
  const rest = sections.filter((s) => s.id !== section.id);
  const at = rest.findIndex((s) => s.id === 'active-problems');
  if (at < 0) return [...rest, section];
  return [...rest.slice(0, at + 1), section, ...rest.slice(at + 1)];
}
